/**
 * Flock telemetry models shared by web and NativeScript apps: Claude quota
 * windows (/api/ryn-db/quota) and per-seat context snapshots
 * (/api/ryn-db/context). Ported from the app-local services in
 * web-ryns-roost so both platforms converge on one copy.
 */

// Claude Team Premium cap estimates. No public Anthropic figures, anchored to
// 1 empirical data point: ryn.ai at ~7.75B observed weekly tokens displayed
// as "38% used" on claude.ai/usage 2026-05-13. Tune these constants when
// other accounts show divergent percentages on the official UI.
//
// Our scanner sums input + output + cache_creation + cache_read tokens, which
// over-counts vs Anthropic's internal accounting (cache reads cost less in
// their billing). Treat these caps as "effective scanner-counted tokens"
// not "Anthropic-billed tokens."
export const TEAM_PREMIUM_WEEKLY_CAP = 20_400_000_000;
export const TEAM_PREMIUM_FIVE_HOUR_CAP = 680_000_000;

/** 'WeeklyFable' (WI 31105) is the model-scoped weekly window: one row per
 * account, sourced from the rendered status line, no transcript token split. */
export type QuotaWindow = 'FiveHour' | 'Weekly' | 'WeeklyFable';

export interface QuotaRow {
  /** Subscription identity; omitted legacy rows belong to Claude. */
  provider?: 'claude' | 'grok' | 'codex' | null;
  id: number;
  accountUpn: string;
  windowType: QuotaWindow;
  firstMessageAt: string | null;
  resetAt: string | null;
  tokenCount: number | null;
  messageCount: number | null;
  reportingHost: string | null;
  reportingHomeId: string | null;
  manualResetOverride: string | null;
  /** The Anthropic API's own used_percentage for this account+window.
   * Since WI 31105 harvested from the seat's RENDERED status line (see
   * `source`); before that from statusline payload files. Authoritative when
   * present; transcript-token cap math is the fallback. Null on rows written
   * before 2026-08-10 or when no reading existed at scan time. */
  apiPercentUsed: number | null;
  /** Where apiPercentUsed came from: 'statusline' (rendered line, WI 31105
   * authoritative), 'payload' (payload-file cross-check), or 'estimate'.
   * Null/undefined on rows written before the 31105 bridge migration. */
  source?: string | null;
  /** When the status line was observed (sweeper clock). Null pre-31105. */
  capturedAt?: string | null;
  /** Statusline-vs-cross-check mismatch, human-readable. A disagreement is a
   * FLAGGED FIELD rendered on the page, never a silent pick (Tim, 2026-08-29).
   * Null when the sources agree or only one spoke. */
  disagreementNote?: string | null;
  lastUpdatedAt: string;
}

/** One stored weekly usage reading (US 34691, GET /ryn-db/quota/samples):
 * server-side history for the 7d projection's 12h slope. `accountUpn` is
 * lower-cased by the bridge. */
export interface QuotaSampleRow {
  provider: 'claude' | 'grok' | 'codex';
  accountUpn: string;
  windowType: QuotaWindow;
  percentUsed: number;
  resetAt: string | null;
  sampledAt: string;
}

export type UsageBand = 'cool' | 'warm' | 'hot' | 'critical';

export interface WindowSummary {
  capturedAt?: string | null;
  tokenCount: number;
  messageCount: number;
  firstMessageAt: string | null;
  effectiveResetAt: string | null;
  /** True when the reset did not come from an API reading or a manual
   * override: a transcript lookback estimate, or the last API reset rolled
   * forward on the weekly cadence. The page marks these "est.". */
  resetEstimated?: boolean;
  activeHomes: number;
  overrideActive: boolean;
  cap: number;
  percentUsed: number;
  band: UsageBand;
  /** Source of the freshest percent ('statusline' | 'payload' | 'estimate' |
   * null for pre-31105 rows). Additive vs the C# WindowRollup parity port,
   * which does not consume it. */
  source: string | null;
  /** Freshest current row's statusline-vs-cross-check mismatch; rendered as a
   * flag on the page, never silently resolved. */
  disagreementNote: string | null;
  /** When NO row is current (no live window), the freshest expired reading so
   * the page can render "no active window (last seen N% at T)" honestly
   * instead of a live-looking 0% (WI 31105, the parts incident). */
  staleObservation: { percent: number; at: string } | null;
}

export interface AccountRollup {
  accountUpn: string;
  fiveHour: WindowSummary;
  weekly: WindowSummary;
  /** Model-scoped weekly window (WI 31105). Percent comes only from the
   * status line (cap 0, no transcript token split exists for it). */
  fable: WindowSummary;
  totalHomes: number;
  lastUpdatedAt: string | null;
}

// Tim's "nobody self-stops without /context > 60%" rule (2026-05-17).
export const CONTEXT_THRESHOLD_PCT = 60;

export type CycleStatus = 'active' | 'cycle_requested' | 'cycle_prepared';

// CC-OBSERVED process liveness, published by the per-machine Control Center.
// DISTINCT from CycleStatus (the cycle-REQUEST lifecycle), the publisher never
// writes cycleStatus, so observed liveness and a pending cycle request coexist.
export type ProcessState = 'running' | 'cycling' | 'reaped' | 'orphaned';

export interface ContextSnapshot {
  id: number;
  instanceId: string;
  accountUpn: string | null;
  percentUsed: number;
  totalTokens: number;
  windowSize: number;
  modelName: string | null;
  turnCount: number | null;
  sessionPath: string | null;
  reportingHost: string | null;
  cycleStatus: CycleStatus;
  observedAt: string;
  cycleRequestedAt: string | null;
  cyclePreparedAt: string | null;
  // CC-federation publisher fields. Null for older rows and for skill-only
  // snapshots that don't carry them (the column set is purely additive).
  fiveHourPercent: number | null;
  sevenDayPercent: number | null;
  idleMinutes: number | null;
  processState: ProcessState | null;
  tmuxWindow: string | null;
  lastActivityAt: string | null;
  // WI 34302 seat condition (running | logged-out | limit-reached |
  // stuck-prompt | unknown) and when it was last written. Absent on older rows.
  seatStatus?: string | null;
  seatStatusObservedAt?: string | null;
}

export type ContextBand = 'safe' | 'warn' | 'over' | 'requested' | 'prepared';

export interface ContextRow extends ContextSnapshot {
  band: ContextBand;
  windowLabel: string;
  ageMinutes: number;
}
